/**
 * Gmail OAuth 2.0 PKCE flow.
 * client_secret never leaves the server - token exchange and refresh are proxied
 * through the backend so the frontend only receives client_id and scopes.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

const AUTH_URL = 'https://accounts.google.com/o/oauth2/v2/auth';
const USERINFO_URL = 'https://www.googleapis.com/oauth2/v2/userinfo';
const REVOKE_URL = 'https://oauth2.googleapis.com/revoke';
const REDIRECT_URI = `${typeof window !== 'undefined' ? window.location.origin : ''}/auth/callback`;

const STORAGE_KEY = 'gmail-extractor-tokens';

export interface GmailProfile {
  email: string;
  name: string;
  picture?: string;
}

interface StoredTokens {
  refreshToken: string;
  userProfile: GmailProfile;
}

function base64url(bytes: Uint8Array): string {
  let str = '';
  for (const b of bytes) str += String.fromCharCode(b);
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function getApiBaseUrl(): string {
  const buildTime = (import.meta as { env?: { VITE_API_URL?: string } }).env?.VITE_API_URL;
  if (buildTime && buildTime !== 'undefined') return buildTime;
  if (typeof window !== 'undefined') {
    const { hostname, protocol } = window.location;
    if (hostname === 'localhost' || hostname === '127.0.0.1') return 'http://localhost:3000';
    return `${protocol}//${hostname}`;
  }
  return 'http://localhost:3000';
}

async function loadCredentials(): Promise<{ client_id: string; scopes: string }> {
  const baseUrl = getApiBaseUrl();
  const res = await fetch(`${baseUrl}/api/v1/gmail-extractor/credentials`);
  if (!res.ok) throw new Error('Failed to load credentials');
  const text = await res.text();
  try {
    return JSON.parse(text);
  } catch {
    throw new Error('Invalid credentials response (expected JSON)');
  }
}

export function useGmailAuth() {
  const [profile, setProfile] = useState<GmailProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [creds, setCreds] = useState<{ client_id: string; scopes: string } | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);
  const [tokenExpiresAt, setTokenExpiresAt] = useState(0);
  const [refreshPromise, setRefreshPromise] = useState<Promise<string | null> | null>(null);

  const getAccessToken = useCallback(
    async (forceRefresh = false): Promise<string | null> => {
      if (!forceRefresh && accessToken && Date.now() < tokenExpiresAt - 60000)
        return accessToken;
      if (!refreshToken || !creds) return null;

      if (refreshPromise) {
        return refreshPromise;
      }

      const doRefresh = async (): Promise<string | null> => {
        const baseUrl = getApiBaseUrl();
        const res = await fetch(`${baseUrl}/api/v1/gmail-extractor/token-refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: refreshToken }),
        });
        const data = await res.json();
        if (data.error) throw new Error(data.error);
        setAccessToken(data.access_token);
        setTokenExpiresAt(Date.now() + (data.expires_in || 3600) * 1000);
        return data.access_token as string;
      };

      const p = doRefresh().finally(() => setRefreshPromise(null));
      setRefreshPromise(p);
      return p;
    },
    [accessToken, creds, refreshToken, tokenExpiresAt, refreshPromise]
  );

  const signIn = useCallback(async (): Promise<GmailProfile> => {
    if (!creds) throw new Error('Credentials not loaded');

    const arr = new Uint8Array(32);
    crypto.getRandomValues(arr);
    const codeVerifier = base64url(arr);

    const encoder = new TextEncoder();
    const hash = await crypto.subtle.digest('SHA-256', encoder.encode(codeVerifier));
    const codeChallenge = base64url(new Uint8Array(hash));

    sessionStorage.setItem('gmail-extractor-pkce', codeVerifier);

    const authUrl = new URL(AUTH_URL);
    authUrl.searchParams.set('client_id', creds.client_id);
    authUrl.searchParams.set('redirect_uri', REDIRECT_URI);
    authUrl.searchParams.set('response_type', 'code');
    authUrl.searchParams.set('scope', creds.scopes);
    authUrl.searchParams.set('code_challenge', codeChallenge);
    authUrl.searchParams.set('code_challenge_method', 'S256');
    authUrl.searchParams.set('access_type', 'offline');
    authUrl.searchParams.set('prompt', 'consent');

    const w = 500;
    const h = 600;
    const left = screen.width / 2 - w / 2;
    const top = screen.height / 2 - h / 2;
    const popup = window.open(
      authUrl.toString(),
      'gmail-auth',
      `width=${w},height=${h},left=${left},top=${top},toolbar=no,menubar=no`
    );

    return new Promise((resolve, reject) => {
      const interval = setInterval(() => {
        try {
          if (!popup || popup.closed) {
            clearInterval(interval);
            reject(new Error('Sign-in window was closed'));
            return;
        }
        const popupUrl = popup.location.href;
        if (popupUrl.startsWith(REDIRECT_URI)) {
          clearInterval(interval);
          const params = new URL(popupUrl).searchParams;
          popup.close();

          const code = params.get('code');
          const err = params.get('error');
          if (err || !code) {
            reject(new Error(err || 'No authorization code received'));
            return;
          }

          const baseUrl = getApiBaseUrl();
          fetch(`${baseUrl}/api/v1/gmail-extractor/token-exchange`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              code,
              code_verifier: codeVerifier,
              redirect_uri: REDIRECT_URI,
            }),
          })
            .then((r) => r.json())
            .then(async (tokens) => {
              if (tokens.error) throw new Error(tokens.error_description || tokens.error);
              setAccessToken(tokens.access_token);
              setRefreshToken(tokens.refresh_token || null);
              setTokenExpiresAt(Date.now() + (tokens.expires_in || 3600) * 1000);

              const profileRes = await fetch(USERINFO_URL, {
                headers: { Authorization: `Bearer ${tokens.access_token}` },
              });
              const p = (await profileRes.json()) as GmailProfile;
              setProfile(p);
              localStorage.setItem(
                STORAGE_KEY,
                JSON.stringify({ refreshToken: tokens.refresh_token, userProfile: p })
              );
              resolve(p);
            })
            .catch(reject);
        }
      } catch {
        // Cross-origin, keep polling
      }
    }, 200);

      setTimeout(() => {
        clearInterval(interval);
        if (popup && !popup.closed) popup.close();
        reject(new Error('Sign-in timed out'));
      }, 120000);
    });
  }, [creds]);

  const signOut = useCallback(async () => {
    if (accessToken) {
      try {
        await fetch(`${REVOKE_URL}?token=${accessToken}`, { method: 'POST' });
      } catch {
        /* ignore */
      }
    }
    setAccessToken(null);
    setRefreshToken(null);
    setTokenExpiresAt(0);
    setProfile(null);
    localStorage.removeItem(STORAGE_KEY);
  }, [accessToken]);

  useEffect(() => {
    let cancelled = false;

    async function init() {
      try {
        const c = await loadCredentials();
        if (cancelled) return;
        setCreds(c);

        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) {
          setLoading(false);
          return;
        }

        const data = JSON.parse(raw) as StoredTokens;
        setRefreshToken(data.refreshToken);
        setProfile(data.userProfile);

        const baseUrl = getApiBaseUrl();
        const res = await fetch(`${baseUrl}/api/v1/gmail-extractor/token-refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: data.refreshToken }),
        });
        const tokens = await res.json();
        if (tokens.error) throw new Error('Token invalid');

        if (cancelled) return;
        setAccessToken(tokens.access_token);
        setTokenExpiresAt(Date.now() + (tokens.expires_in || 3600) * 1000);

        const testRes = await fetch(
          'https://gmail.googleapis.com/gmail/v1/users/me/profile',
          { headers: { Authorization: `Bearer ${tokens.access_token}` } }
        );
        if (!testRes.ok) throw new Error('Gmail API access denied');
      } catch (e) {
        if (!cancelled) {
          setRefreshToken(null);
          setProfile(null);
          setError(e instanceof Error ? e.message : 'Auth failed');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void init();
    return () => {
      cancelled = true;
    };
  }, []);

  // Stable identity so consumers (eg. ProgressScreen useEffect) don't restart
  // when token state changes cause getAccessToken to get a new closure.
  const getAccessTokenRef = useRef(getAccessToken);
  getAccessTokenRef.current = getAccessToken;
  const stableGetAccessToken = useCallback(
    (force?: boolean) => getAccessTokenRef.current(force),
    []
  );

  return {
    profile,
    loading,
    error,
    isSignedIn: !!profile && !!refreshToken,
    signIn,
    signOut,
    getAccessToken: creds ? stableGetAccessToken : async () => null,
  };
}
